/* =============================================================================
   yojo-gift-guard — the fail-closed half of the gift threshold (2026-08-12).

   INVARIANT (conversion rebuild, owner-directed): anything the theme called
   "free" must have a server-returned line price of ₪0, and a failed reward
   never leaves a paid gift line behind.

   The failure this guards (reproduced live 2026-08-12): the gift is added at
   ₪0 via the 'YOJO Gift' automatic BxGy; the customer then applies an order
   code whose combinability evicts the automatic discount (or the basket drops
   below the ₪250 bar), and the gift line silently reverts to its real price.
   snippets/yojo-gift-threshold.liquid already NAMES that state (the
   "stalled" row); this module is the remedy: it REMOVES the charged line.

   Scope guard: only lines carrying properties._yojo_src == 'gift-threshold'
   are ever touched — that property is written exclusively by the gift-add
   button, so a pineapple the customer chose to buy as a product (a separate
   cart line, since properties differ) is never removed.

   Interlock: the <script> tag for this file is rendered only while
   settings.upsell_gift_enable is on (layout/theme.liquid) — same kill switch
   as the promise itself. No settings knowledge lives here.

   Loop safety: one removal attempt per cart state. The attempt key is the
   line key + the cart's total, so a re-render that did not change anything
   does not retry, while a genuinely new charged-gift state (new add) does.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__yojoGiftGuard) return;
  window.__yojoGiftGuard = 1;

  var SRC = 'gift-threshold';
  var attempted = null;   /* last cart-state key we acted on */
  var running = false;
  /* 2026-09-26 (R7): read while this script is executing — currentScript is
     null inside later callbacks. layout/theme.liquid puts the threshold on the
     <script> tag itself, so it is known even when the drawer block is absent. */
  var SELF = document.currentScript;

  /* ── auto-add (2026-08-14, Shir feedback round 2, owner-approved) ──────
     The gift is no longer click-to-add: when the qualifying subtotal crosses
     the threshold, this module adds the gift line itself. The variant id and
     threshold ride on [data-yojo-gift-threshold]'s data attributes (written
     by the snippet), so the settings interlock stays where it was.

     Loop safety — the add must never fight the removal above:
       * AUTO_DONE (sessionStorage) is set BEFORE the add request. While it is
         set, no re-add happens — so when the BxGy is evicted by a coupon and
         the sweep removes the charged line, the state settles (qualify still
         over the bar, flag set, no retry).
       * The flag is cleared only when the cart is observed BELOW the
         threshold, re-arming the add for a genuine re-crossing. A customer
         who removed the gift by hand while staying over the bar is respected
         until the cart drops under it.
       * final_line_price is irrelevant at add time: if the discount is
         missing the added line comes back charged, and the very next sweep
         removes it — fail-closed exactly as before.

     2026-09-26 (R7, audit website-az-2026-09-25: 4 of 32 eligible orders had
     no gift). Two fixes, same rules:
       * The re-arm ran only when [data-yojo-gift-threshold] rendered, and the
         block does not render under the ₪150 minimum or in an empty cart. A
         basket that went ₪260 → ₪140 (or was emptied, or ordered) → ₪260 kept
         the flag and never got the gift again. The below-threshold check now
         runs first, with the threshold from the <script> tag; the block
         stays the gate for the ADD only.
       * The flag now records WHY it is set, so checkout can tell a gift that
         must stay away from one that simply has not landed yet:
           'pending'  add request sent (set before it, as before)
           'added'    add confirmed; a missing line later = the customer (or
                      another tab) removed it: respected, never re-added
           'guard'    the sweep removed a CHARGED gift while the basket was
                      over the bar (discount evicted by a code): never
                      re-added, the loop-safety rule above
           'failed'   the add request failed: no background retry (never
                      hammer /cart/add), one retry at checkout
         Legacy '1' (pre-2026-09-26 sessions) reads as 'added'. */
  var AUTO_DONE = 'yojoGiftAutoDone';
  var doneMem = ''; /* fallback when sessionStorage is blocked */
  var inflightAdd = null; /* the one gift add on the wire, shared with checkout */

  function readDone() {
    try {
      var s = sessionStorage.getItem(AUTO_DONE);
      if (s !== null) return s === '1' ? 'added' : s;
    } catch (e) { /* storage-less: memory flag rules */ }
    return doneMem;
  }
  function writeDone(v) {
    doneMem = v || '';
    try {
      if (v) sessionStorage.setItem(AUTO_DONE, v);
      else sessionStorage.removeItem(AUTO_DONE);
    } catch (e) {}
  }

  function thresholdCents() {
    var src = SELF || document.querySelector('script[data-gift-threshold-cents]');
    var el = document.querySelector('[data-yojo-gift-threshold]');
    var v = parseInt((src && src.getAttribute('data-gift-threshold-cents')) ||
      (el && el.getAttribute('data-gift-threshold-cents')), 10);
    return v > 0 ? v : 0;
  }
  function giftLineOf(cart) {
    return (cart.items || []).filter(function (i) {
      return i.properties && i.properties._yojo_src === SRC;
    })[0];
  }
  /* Qualifying amount mirrors the snippet: pre-discount total minus the
     gift's own line, so the free gift never funds its own threshold. */
  function qualifyOf(cart) {
    var g = giftLineOf(cart);
    return cart.original_total_price - (g ? g.original_line_price : 0);
  }
  /* The one rule for "should the gift be added now". Background sweeps add
     only on a clean flag; checkout also retries a failed or orphaned add. */
  function wantsGift(cart, thr, done, atCheckout) {
    if (!thr || giftLineOf(cart) || qualifyOf(cart) < thr) return false;
    if (!done) return true;
    return !!atCheckout && (done === 'failed' || done === 'pending');
  }

  /* After the sweep removed a CHARGED gift line (`cart` = the state it read).
     Over the bar that means the discount was evicted by a code: 'guard' keeps
     it from being re-added (loop safety). Under the bar, re-arm. */
  function markSwept(cart) {
    var thr = thresholdCents();
    writeDone(thr && qualifyOf(cart) >= thr ? 'guard' : '');
  }

  function addGift(vid) {
    if (inflightAdd) return inflightAdd;
    writeDone('pending');
    /* Checkout hardening 2026-08-25: announce this mutation to the shared
       in-flight counter, or window.refreshCart (focus/pageshow) can read the
       cart mid-add and repaint the pre-gift state over the fresh render. */
    window._yojoCartMutationsInFlight = (window._yojoCartMutationsInFlight || 0) + 1;
    inflightAdd = fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: vid, quantity: 1, properties: { _yojo_src: SRC } }),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/add ' + r.status);
      writeDone('added');
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'yojo_gift_auto_added' });
      } catch (e) { /* analytics never breaks the cart */ }
      return true;
    }).catch(function () { return false; }).then(function (ok) {
      if (!ok) writeDone('failed');
      inflightAdd = null;
      window._yojoCartMutationsInFlight = Math.max(0, (window._yojoCartMutationsInFlight || 1) - 1);
      return ok;
    });
    return inflightAdd;
  }

  /* Runs inside the cart queue (see queued() below). Resolves to what to do
     once the queue lets go: a repaint through window.refreshCart would be
     skipped while the queue still counts this task as in flight. */
  function autoAdd(cart) {
    var thr = thresholdCents();
    if (!thr) return;
    if (qualifyOf(cart) < thr) { writeDone(''); return; } /* re-arm, block or no block */
    var el = document.querySelector('[data-yojo-gift-threshold]');
    if (!el) return;
    var vid = parseInt(el.getAttribute('data-gift-variant'), 10);
    if (!vid) return;
    var done = readDone();
    /* A free gift line is in the cart (this tab's add, or another tab's): from
       here on its absence over the bar means someone removed it. */
    if (giftLineOf(cart)) { if (done !== 'added' && !inflightAdd) writeDone('added'); return; }
    if (inflightAdd || !wantsGift(cart, thr, done, false)) return;
    return addGift(vid).then(function (ok) {
      if (!ok) return; /* 'failed': the next below-threshold observation or checkout re-arms */
      return function () {
        rerender();
        /* Re-sweep so a charged add (discount missing) is removed promptly. */
        schedule();
      };
    });
  }

  /* S-01 (live sweep 2026-10-14): the gift add re-keys the shopper's own
     lines (Shopify splits the bags that qualify onto their own line), so a
     quantity change already on the wire landed on a stale key: 3 taps gave
     10 bags. Every read-decide-write here now runs in component-cart.js's one
     cart queue, after any quantity change queued before it and before any
     queued after it. Without the queue (the script on its own) it runs as
     before. */
  function queued(task) {
    var q = window.yojoCartQueue;
    if (q && typeof q.run === 'function') return q.run(task);
    return Promise.resolve().then(task);
  }

  /* ── time budget ───────────────────────────────────────────────────────
     A STALLED request is not a failed one. fetch() rejects on error but never
     settles on a hang, so a bare .catch() cannot restore the customer — the
     continuation that resubmits checkout simply never runs.

     Measured on live 2026-08-12T23:57Z (evidence:
     output/live-stabilizer/artifacts/20260812T235725Z-claude-discovery/
     checkout-gate-result.json), cart ₪168 + valid phone, 390x844:
       * /cart.js aborts  -> checkout reached      (fail-open already worked)
       * /cart.js stalls  -> checkout UNREACHABLE, and the retry click is
                             swallowed too, because gateBusy stays latched.
     Every non-empty cart renders [data-yojo-gift-threshold], so this gate sits
     on the critical path of 100% of checkouts; only the stall was unbounded.

     Bounding the gate in time turns a hang into exactly the fail-open path the
     abort case already proves works. */
  var GATE_TIMEOUT_MS = 2500;   /* checkout: the customer is waiting */
  var CHECK_TIMEOUT_MS = 8000;  /* background sweep: nobody is blocked */

  function after(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function announce(msg) {
    /* Reuse the drawer's polite announcer when present; degrade to a plain
       inline notice inside the gift block. */
    var live = document.querySelector('[data-shop-live], [aria-live="polite"]');
    if (live) live.textContent = msg;
    document.querySelectorAll('[data-yojo-gift-threshold]').forEach(function (el) {
      var n = el.querySelector('[data-gift-guard-note]');
      if (!n) {
        n = document.createElement('p');
        n.setAttribute('data-gift-guard-note', '');
        n.className = 'yojo-gift-threshold__row yojo-gift-threshold__row--stalled';
        el.appendChild(n);
      }
      n.textContent = msg;
    });
  }

  function rerender() {
    /* component-cart.js owns re-rendering — window.refreshCart is the one
       canonical path: it fetches helper-cart RELATIVE to the current page (so
       /cart gets the page form, everywhere else the drawer form), re-binds the
       qty/remove listeners via ajaxifyCartItems, updates the header, and swaps
       the subtotal through yojoReplaceCartSubtotal (which syncs the data-*
       attributes yojoCartTruth reads).

       The absolute '/?section_id=helper-cart' this used before rendered the
       section against the HOME template, so on /cart it overwrote the page
       form with DRAWER markup; and the bare innerHTML swap dropped the remove/
       qty listeners, reverting the remove link to a full-page navigation. */
    try {
      var done = function () {
        document.dispatchEvent(new CustomEvent('yojo:cart:refreshed', { bubbles: true }));
      };
      if (window.refreshCart) {
        var p = window.refreshCart({ open: false });
        (p && p.then ? p : Promise.resolve()).then(done, done);
        return;
      }
      fetch('?section_id=helper-cart').then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var form = doc.querySelector('#AjaxCartForm');
        var sub = doc.querySelector('#AjaxCartSubtotal');
        var curForm = document.querySelector('#AjaxCartForm');
        var curSub = document.querySelector('#AjaxCartSubtotal');
        if (form && curForm) {
          curForm.innerHTML = form.innerHTML;
          if (curForm.ajaxifyCartItems) curForm.ajaxifyCartItems();
          if (curForm.updateHeader) curForm.updateHeader();
        }
        if (sub && curSub) curSub.innerHTML = sub.innerHTML;
        done();
      });
    } catch (e) { /* rendering is cosmetic here; the cart itself is already right */ }
  }

  function check() {
    if (running) return;
    running = true;
    /* Same stall hazard, lesser blast radius: a hung sweep would latch
       `running` and silently disable the guard for the rest of the page-load.
       Release the latch on the deadline so a later cart change re-arms it. */
    after(CHECK_TIMEOUT_MS).then(function () { running = false; });
    queued(sweep)
      .then(function (then) { running = false; if (typeof then === 'function') then(); })
      .catch(function () { running = false; /* fail open on network: never mutate on uncertainty */ });
  }

  /* One read, one decision, at most one write, inside the cart queue. */
  function sweep() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); })
      .then(function (cart) {
        var bad = (cart.items || []).filter(function (i) {
          return i.properties && i.properties._yojo_src === SRC && i.final_line_price > 0;
        });
        if (!bad.length) return autoAdd(cart);
        var key = bad[0].key + '|' + cart.total_price;
        if (attempted === key) return;
        attempted = key;
        /* Same in-flight announcement as autoAdd: the removal is a mutation. */
        window._yojoCartMutationsInFlight = (window._yojoCartMutationsInFlight || 0) + 1;
        var decremented = false;
        function dec() {
          if (decremented) return;
          decremented = true;
          window._yojoCartMutationsInFlight = Math.max(0, (window._yojoCartMutationsInFlight || 1) - 1);
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: bad[0].key, quantity: 0 }),
        }).then(function (r) {
          dec();
          if (!r.ok) throw new Error('cart/change ' + r.status);
          markSwept(cart);
          return function () {
            announce('המתנה הוסרה מהעגלה כי ההנחה שלה לא חלה יחד עם הקוד שהוזן. הסכום עודכן.');
            rerender();
            try {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'yojo_gift_guard_removed', reason: 'charged_gift_line' });
            } catch (e) { /* analytics never breaks the cart */ }
          };
        }).catch(function (e) { dec(); throw e; });
      });
  }

  /* ── checkout gate ─────────────────────────────────────────────────────
     INVARIANT: checkout cannot begin from an unresolved gift state. The
     removal above is async; without this, a customer can click checkout in
     the window between applying an evicting code and the guard's cart/change
     round-trip, and pay for the "gift". Same composition idiom as
     yojoCartTruth and yojoCartPhoneGate: capture-phase, preventDefault only,
     never toggles the button. On interception: re-verify against /cart.js,
     remove a charged gift line if present, then resubmit programmatically.
     Network failure OR a stall resubmits anyway (see the time budget above) —
     a gate that can permanently block checkout is a worse defect than the one
     it guards.

     2026-09-26 (R7): the gate also closes the other half of the gift promise.
     The background add runs 800 ms after a cart change plus two round trips,
     so a tap on checkout right after crossing the bar reached checkout
     without the gift. Now, inside the SAME 2.5 s budget: an add already on
     the wire is awaited, and an eligible basket (over the bar, no gift line,
     flag not 'added'/'guard') gets the add here. An ineligible basket does
     exactly what it did before: one /cart.js read, then checkout. Nothing
     here can wait past the deadline; a slow or failed add fails open. */
  var gateBusy = false;
  var passThrough = false;
  function resolveThenCheckout(form, submitter) {
    var el = document.querySelector('[data-yojo-gift-threshold]');
    var vid = el ? parseInt(el.getAttribute('data-gift-variant'), 10) : 0;
    /* S-01: queued, so a "+" tapped just before checkout lands first and the
       gift decision reads the cart after it. Still inside the same budget. */
    var resolving = queued(function () {
      return Promise.resolve(inflightAdd)
        .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
        .then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); })
        .then(function (cart) {
          var bad = (cart.items || []).filter(function (i) {
            return i.properties && i.properties._yojo_src === SRC && i.final_line_price > 0;
          });
          if (!bad.length) {
            if (vid && wantsGift(cart, thresholdCents(), readDone(), true)) return addGift(vid);
            return;
          }
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: bad[0].key, quantity: 0 }),
          }).then(function () {
            markSwept(cart);
            announce('המתנה הוסרה מהעגלה כי ההנחה שלה לא חלה יחד עם הקוד שהוזן. הסכום עודכן.');
            rerender();
          });
        });
    })
      .catch(function () { /* uncertainty must not strand the customer */ });

    /* The resolution above is best-effort; the handoff below is not optional.
       Race it against a hard deadline so a hung request cannot outlive the
       customer's patience, then continue on whichever settles first. A slow
       resolution that lands after the deadline is harmless: either checkout
       already began, or the next page-load's sweep removes the charged line. */
    Promise.race([resolving, after(GATE_TIMEOUT_MS)])
      .then(function () {
        gateBusy = false;
        passThrough = true;   /* the resubmit below must not be re-intercepted */
        /* CRITICAL: resubmit WITH the original submitter. requestSubmit()
           without it drops name=checkout, so the POST becomes a plain cart
           update and the customer bounces back to /cart — checkout would be
           unreachable on every gift-enabled cart (caught by the browser-pane
           probe 2026-08-12 17:05). */
        if (form && form.requestSubmit && submitter && document.contains(submitter)) form.requestSubmit(submitter);
        else if (form && form.requestSubmit) {
          var btn = form.ownerDocument.querySelector('button[name="checkout"][form="' + form.id + '"], #CheckOut');
          btn ? form.requestSubmit(btn) : form.requestSubmit();
        }
        else if (form) form.submit();
      });
  }
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form#cart')) return;
    /* checkout submits carry the checkout submitter; plain cart updates don't */
    var submitter = event.submitter;
    if (submitter && submitter.name !== 'checkout') return;
    if (passThrough) { passThrough = false; return; }
    /* Composition (2026-08-25): if a sibling gate (min-order / phone / zone)
       already blocked this submit, respect it. Intercepting anyway would spend
       the 2.5s resolve and then resubmit into the same block — the customer's
       click would appear to do nothing for the whole round trip. */
    if (event.defaultPrevented) return;
    if (gateBusy) { event.preventDefault(); event.stopPropagation(); return; }
    /* Only intercept while the gift feature is live on this surface — the
       block renders exactly when the threshold system is on. A checkout with
       the feature off stays a plain, untouched submit. */
    if (!document.querySelector('[data-yojo-gift-threshold]')) return;
    event.preventDefault();
    event.stopPropagation();
    gateBusy = true;
    resolveThenCheckout(form, submitter);
  }, true);

  /* Check whenever the cart could have changed state. */
  var debounce;
  function schedule() { clearTimeout(debounce); debounce = setTimeout(check, 800); }
  document.addEventListener('DOMContentLoaded', schedule);
  document.addEventListener('yojo:cart:refreshed', schedule);
  /* 'cart:refresh' listener removed 2026-08-25 — nothing ever dispatched it.
     The real mutation announcements are 'cart:add'/'cart:update'
     (yojo-analytics-events.js) and 'cart:updated' (add paths + component-cart). */
  document.addEventListener('cart:updated', schedule);
  /* Post-live cart battery, 2026-08-27 (F3): every trigger above fires only
     while THIS tab is the one mutating. A shopper who qualifies in tab A, drops
     back under the threshold in tab B, then returns to tab A and goes straight
     to /checkout carried a CHARGED "gift" through — the guard never re-ran
     because tab A saw no mutation and the form-submit gate was bypassed by a
     direct navigation. Re-checking when a stale tab becomes visible again
     closes that path: the shopper has to focus the tab before they can act in
     it. (A server-side validation is the complete answer; this removes the
     reachable case from the storefront.) */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule();
  });
  window.addEventListener('pageshow', schedule);
  /* component-cart.js replaces #AjaxCartForm innerHTML on every mutation —
     observe it so codes applied at /discount/<code> or qty edits re-check. */
  var mo = new MutationObserver(schedule);
  function arm() {
    var t = document.querySelector('#AjaxCartForm') || document.querySelector('#AjaxCartSubtotal');
    if (t) mo.observe(t, { childList: true, subtree: true });
  }
  if (document.readyState !== 'loading') { arm(); schedule(); }
  else document.addEventListener('DOMContentLoaded', arm);
})();
